import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './HTMLTemplateEditor.css';

const HTMLTemplateEditor = ({ isOpen, onClose, initialValue = '', onSave, fields = [] }) => {
  const [content, setContent] = useState(initialValue);
  const [isCodeView, setIsCodeView] = useState(false);
  const [htmlCode, setHtmlCode] = useState(initialValue);
  const [selectedField, setSelectedField] = useState('');
  const quillRef = useRef(null);
  const codeTextareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setContent(initialValue);
      setHtmlCode(initialValue);
    }
  }, [isOpen, initialValue]);

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  const handleToggleView = () => {
    if (isCodeView) {
      // Switching from code to WYSIWYG - update content from code
      setIsCodeView(false);
      // Delay content update to ensure view switches first
      setTimeout(() => {
        setContent(htmlCode);
      }, 0);
    } else {
      // Switching from WYSIWYG to code - update code from content
      setHtmlCode(content);
      setIsCodeView(true);
    }
  };

  const handleSave = () => {
    const finalContent = isCodeView ? htmlCode : content;
    onSave(finalContent);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleContentChange = (value) => {
    setContent(value);
    if (!isCodeView) {
      setHtmlCode(value);
    }
  };

  const handleCodeChange = (e) => {
    setHtmlCode(e.target.value);
  };

  const insertFieldToken = () => {
    if (!selectedField) return;

    const token = `{{${selectedField}}}`;

    if (isCodeView) {
      // Insert at cursor position in code view
      const textarea = codeTextareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newText = htmlCode.substring(0, start) + token + htmlCode.substring(end);
        setHtmlCode(newText);
        // Set cursor position after inserted token
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + token.length;
          textarea.focus();
        }, 0);
      }
    } else {
      // Insert in WYSIWYG view
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection();
        if (range) {
          editor.insertText(range.index, token);
          editor.setSelection(range.index + token.length);
        }
      }
    }
  };

  const insertConditionalTemplate = () => {
    if (!selectedField) return;

    const template = `{{IF {${selectedField}}='value' ?'true text':'false text'}}`;

    if (isCodeView) {
      const textarea = codeTextareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newText = htmlCode.substring(0, start) + template + htmlCode.substring(end);
        setHtmlCode(newText);
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + template.length;
          textarea.focus();
        }, 0);
      }
    } else {
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection();
        if (range) {
          editor.insertText(range.index, template);
          editor.setSelection(range.index + template.length);
        }
      }
    }
  };

  // Get available fields for dropdown
  const availableFields = fields.filter(f =>
    !['group', 'heading', 'styledText', 'html', 'htmltemplateeditor'].includes(f.type)
  );

  if (!isOpen) return null;

  return (
    <div className="html-template-overlay">
      <div className="html-template-modal">
        <div className="html-template-header">
          <h3>HTML Template Editor</h3>
          <button className="html-template-close-btn" onClick={handleCancel}>×</button>
        </div>

        <div className="html-template-controls">
          <div className="field-insertion-controls">
            <label htmlFor="field-select">Insert Field:</label>
            <select
              id="field-select"
              value={selectedField}
              onChange={(e) => setSelectedField(e.target.value)}
              className="field-select"
            >
              <option value="">-- Select a field --</option>
              {availableFields.map(field => (
                <option key={field.id} value={field.name}>
                  {field.label} ({field.name})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={insertFieldToken}
              disabled={!selectedField}
              className="insert-btn insert-field-btn"
              title="Insert field value: {{fieldname}}"
            >
              Insert Field Value
            </button>
            <button
              type="button"
              onClick={insertConditionalTemplate}
              disabled={!selectedField}
              className="insert-btn insert-conditional-btn"
              title="Insert conditional logic: {{IF {field}='value' ?'true':'false'}}"
            >
              Insert Conditional
            </button>
          </div>

          <button
            className={`html-template-toggle-btn ${!isCodeView ? 'active' : ''}`}
            onClick={handleToggleView}
            type="button"
          >
            {isCodeView ? 'Switch to WYSIWYG' : 'Switch to HTML Code'}
          </button>
        </div>

        <div className="html-template-content">
          {!isCodeView ? (
            <ReactQuill
              key="template-wysiwyg-view"
              ref={quillRef}
              theme="snow"
              value={content}
              onChange={handleContentChange}
              modules={modules}
              formats={formats}
              placeholder="Enter your HTML template here. Use the field dropdown above to insert field tokens..."
            />
          ) : (
            <textarea
              key="template-code-view"
              ref={codeTextareaRef}
              className="html-template-code-editor"
              value={htmlCode}
              onChange={handleCodeChange}
              placeholder="Enter HTML template code here. Use {{fieldname}} for field values and {{IF {field}='value' ?'true':'false'}} for conditionals..."
              spellCheck={false}
            />
          )}
        </div>

        <div className="html-template-footer">
          <div className="template-help">
            <strong>Token Syntax:</strong>
            <code>{'{{fieldname}}'}</code> for values,
            <code>{"{{IF {field}='value' ?'yes':'no'}}"}</code> for conditionals
          </div>
          <div className="template-actions">
            <button className="html-template-cancel-btn" onClick={handleCancel} type="button">
              Cancel
            </button>
            <button className="html-template-save-btn" onClick={handleSave} type="button">
              Save Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HTMLTemplateEditor;
